import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit,
  where,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { db } from '../firebase/config';
import {
  FiUsers,
  FiPackage,
  FiTrendingUp,
  FiSearch,
  FiRefreshCw,
  FiPlusCircle,
  FiLink2,
  FiFilter,
  FiSettings,
  FiImage,
  FiBell,
  FiLogOut
} from 'react-icons/fi';
import ImageSelector from '../components/ImageSelector';
import MusicUploader from '../components/MusicUploader';
import TextEditor from '../components/TextEditor';
import { normalizeMediaUrl, FUNCTIONS_URL } from '../config/apiConfig';
import './Dashboard.css';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
/** ============================
 * CONFIGS
 * ============================ */
const MAX_USERS_FOR_PACKS = 200;

// ajuste o package ID do Android se necessário
const ANDROID_PACKAGE = 'com.talkaboutit';
const IOS_APP_STORE_BASE_URL = 'https://apps.apple.com/app/apple-store/id6762285625?pt=127218316';

const PRESETS = {
  last7: 'last7',
  last30: 'last30',
  thisMonth: 'thisMonth',
  all: 'all',
  custom: 'custom',
};

// utils de data
function startOfDay(date) { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; }
function endOfDay(date) { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; }
function monthBounds(d = new Date()) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { first: startOfDay(first), last: endOfDay(last) };
}

// helpers para influencer links
const slugify = (s = '') =>
  s
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const suggestCode = (name = '') => {
  const base = name
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');
  const core = base.slice(0, 12) || 'INFLUENCER';
  return `${core}123`;
};

const buildPlayLink = ({ appId, code, utm_source, utm_campaign }) => {
  const qs = [
    `code=${encodeURIComponent(code || '')}`,
    `utm_source=${encodeURIComponent(utm_source || '')}`,
  ];
  if (utm_campaign) qs.push(`utm_campaign=${encodeURIComponent(utm_campaign)}`);
  const refStr = qs.join('&');
  return `https://play.google.com/store/apps/details?id=${appId}&referrer=${encodeURIComponent(refStr)}`;
};

const buildAppStoreLink = ({ baseUrl, code }) => {
  if (!baseUrl?.trim()) return '';

  try {
    const url = new URL(baseUrl.trim());
    if (url.hostname !== 'apps.apple.com') return '';

    // O campaign token da Apple aceita no maximo 30 caracteres.
    url.searchParams.set('ct', (code || '').trim().slice(0, 30));
    if (!url.searchParams.has('mt')) url.searchParams.set('mt', '8');
    return url.toString();
  } catch {
    return '';
  }
};

// hook simples de viewport
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return {
    width: w,
    isMobile: w <= 640,
    isTablet: w > 640 && w <= 1024,
    isDesktop: w > 1024,
  };
}

export default function Dashboard() {
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // ==========================
  // ESTADOS GERAIS / MÉTRICAS
  // ==========================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [totalUsers, setTotalUsers] = useState(0);
  const [topPacks, setTopPacks] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [packsData, setPacksData] = useState([]);

  // ==========================
  // REFERRER ANALYTICS + FILTROS
  // ==========================
  const [refTotal, setRefTotal] = useState(0);
  const [preset, setPreset] = useState(PRESETS.all);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [fSource, setFSource] = useState('');
  const [fCode, setFCode] = useState('');
  const [fCampaign, setFCampaign] = useState('');

  const [refDocs, setRefDocs] = useState([]);
  
  // Novos controles de filtro e ordenação
  const [showOnlyReferrer, setShowOnlyReferrer] = useState(false);
  const [sortBy, setSortBy] = useState('updatedAt_desc'); // updatedAt_desc, updatedAt_asc, email_asc, email_desc
  const [userSearch, setUserSearch] = useState(''); // Busca em tempo real

  // Tabela de usuários por referrer
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ==========================
  // INFLUENCER LINKS (form/list)
  // ==========================
  const [infName, setInfName] = useState('');
  const [infCode, setInfCode] = useState('');
  const [infSource, setInfSource] = useState('');
  const [infCampaign, setInfCampaign] = useState('');
  const [infSaving, setInfSaving] = useState(false);
  const [infError, setInfError] = useState('');
  const [infOk, setInfOk] = useState('');
  const [infLoadingList, setInfLoadingList] = useState(true);
  const [influencers, setInfluencers] = useState([]);

  // Imagem de Login
  const [loginImage, setLoginImage] = useState('');
  const [savingLoginImage, setSavingLoginImage] = useState(false);
  
  // Imagem de Home
  const [homeImage, setHomeImage] = useState('');
  const [savingHomeImage, setSavingHomeImage] = useState(false);
  
  // Música de Fundo
  const [backgroundMusic, setBackgroundMusic] = useState('');
  const [savingMusic, setSavingMusic] = useState(false);
  const [musicSuccess, setMusicSuccess] = useState('');
  const [musicError, setMusicError] = useState('');
  
  // Texto da Home
  const [homeText, setHomeText] = useState('');
  const [savingHomeText, setSavingHomeText] = useState(false);

  // Estatísticas das Lojas (Google Play / App Store)
  const [storeStats, setStoreStats] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState('');
  const [storePeriod, setStorePeriod] = useState('90'); // dias: '7','15','30','90','custom'
  const [storeFrom, setStoreFrom] = useState(''); // YYYY-MM-DD
  const [storeTo, setStoreTo] = useState('');   // YYYY-MM-DD
  const [textSuccess, setTextSuccess] = useState('');
  const [textError, setTextError] = useState('');

  // Modal de Notificações
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifSendToAll, setNotifSendToAll] = useState(true);
  const [notifUserSearch, setNotifUserSearch] = useState('');
  const [notifSelectedUsers, setNotifSelectedUsers] = useState(new Set());
  const [notifSending, setNotifSending] = useState(false);
  const [notifMessage, setNotifMessage] = useState({ type: '', text: '' });

  function handleGerenciarPacks() {
    // pega o primeiro selecionado (ou vários, se quiser)
    const selected = refFiltered.filter(r => selectedIds.has(r.id));
    if (selected.length === 0) return;

    const email = selected[0].email; // aqui você pode mudar para r.id se preferir

    navigate('/admin/pacotes', { state: { email } });
  }

  // Usuários filtrados para notificação (busca em tempo real)
  const notifFilteredUsers = useMemo(() => {
    if (!notifUserSearch) return refDocs;
    
    const search = notifUserSearch.toLowerCase();
    return refDocs.filter(user => 
      user.email?.toLowerCase().includes(search) ||
      user.id?.toLowerCase().includes(search)
    );
  }, [refDocs, notifUserSearch]);

  function toggleNotifUser(userId) {
    setNotifSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function sendNotification() {
    if (!notifTitle.trim() || !notifBody.trim()) {
      setNotifMessage({ type: 'error', text: 'Título e mensagem são obrigatórios' });
      return;
    }

    if (!notifSendToAll && notifSelectedUsers.size === 0) {
      setNotifMessage({ type: 'error', text: 'Selecione pelo menos um usuário' });
      return;
    }

    try {
      setNotifSending(true);
      setNotifMessage({ type: '', text: '' });

      // Coletar tokens dos usuários
      let tokens = [];
      if (notifSendToAll) {
        // Todos os usuários que têm token
        tokens = refDocs
          .filter(user => user.fcmToken)
          .map(user => user.fcmToken);
      } else {
        // Apenas usuários selecionados que têm token
        tokens = refDocs
          .filter(user => notifSelectedUsers.has(user.id) && user.fcmToken)
          .map(user => user.fcmToken);
      }

      if (tokens.length === 0) {
        setNotifMessage({ 
          type: 'error', 
          text: 'Nenhum usuário selecionado tem token FCM. Eles precisam fazer login no app primeiro.' 
        });
        setNotifSending(false);
        return;
      }

      const API_URL = 'https://sendnotification-ct23jdnsvq-uc.a.run.app';
      
      const payload = {
        title: notifTitle,
        body: notifBody,
        tokens: tokens, // Enviar tokens diretamente
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Verificar se a resposta é OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro HTTP:', response.status, errorText);
        throw new Error(`Erro HTTP ${response.status}: ${errorText.substring(0, 100)}`);
      }

      const data = await response.json();

      // Log debug no console
      if (data.debug) {
        console.log('📋 Debug da API:', data.debug);
      }

      if (data.success) {
        setNotifMessage({ 
          type: 'success', 
          text: `Notificação enviada para ${data.sent} usuário(s)` 
        });
        setNotifTitle('');
        setNotifBody('');
        setNotifSelectedUsers(new Set());
        setTimeout(() => setShowNotificationModal(false), 2000);
      } else {
        // Mostrar debug no erro
        const debugInfo = data.debug ? '\n\nDebug:\n' + data.debug.join('\n') : '';
        setNotifMessage({ 
          type: 'error', 
          text: data.message + debugInfo
        });
        console.error('Erro detalhado:', data);
      }
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      setNotifMessage({ 
        type: 'error', 
        text: error.message || 'Erro ao enviar notificação. Verifique o console.' 
      });
    } finally {
      setNotifSending(false);
    }
  }

  // auto-sugestão code/source a partir do nome
  useEffect(() => {
    if (!infName) { setInfCode(''); setInfSource(''); return; }
    if (!infCode) setInfCode(suggestCode(infName));
    if (!infSource) setInfSource(slugify(infName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infName]);

  const infPreviewLink = useMemo(() => buildPlayLink({
    appId: ANDROID_PACKAGE,
    code: infCode,
    utm_source: infSource,
    utm_campaign: infCampaign
  }), [infCode, infSource, infCampaign]);

  const infPreviewIosLink = useMemo(() => buildAppStoreLink({
    baseUrl: IOS_APP_STORE_BASE_URL,
    code: infCode,
  }), [infCode]);

  async function loadStartImage() {
    try {
      // Carregar imagem de login
      const loginDocRef = doc(db, 'settings', 'loginImage');
      const loginDocSnap = await getDoc(loginDocRef);
      if (loginDocSnap.exists()) {
        setLoginImage(normalizeMediaUrl(loginDocSnap.data()?.url || ''));
      }
      
      // Carregar imagem de home
      const homeDocRef = doc(db, 'settings', 'homeImage');
      const homeDocSnap = await getDoc(homeDocRef);
      if (homeDocSnap.exists()) {
        setHomeImage(normalizeMediaUrl(homeDocSnap.data()?.url || ''));
      }
      
      // Carregar música de fundo
      const musicDocRef = doc(db, 'settings', 'backgroundMusic');
      const musicDocSnap = await getDoc(musicDocRef);
      if (musicDocSnap.exists()) {
        setBackgroundMusic(normalizeMediaUrl(musicDocSnap.data()?.url || ''));
      }
      
      // Carregar texto da home
      const textDocRef = doc(db, 'settings', 'homeText');
      const textDocSnap = await getDoc(textDocRef);
      if (textDocSnap.exists()) {
        setHomeText(textDocSnap.data()?.text || '');
      }
    } catch (e) {
      console.error('Erro ao carregar configurações:', e);
    }
  }

  async function saveLoginImage(imageUrl) {
    setSavingLoginImage(true);
    try {
      await setDoc(doc(db, 'settings', 'loginImage'), {
        url: imageUrl,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setLoginImage(imageUrl);
      setInfOk('Imagem de login salva com sucesso!');
      setTimeout(() => setInfOk(''), 2000);
    } catch (e) {
      console.error('Erro ao salvar imagem de login:', e);
      setInfError('Erro ao salvar imagem de login.');
      setTimeout(() => setInfError(''), 2000);
    } finally {
      setSavingLoginImage(false);
    }
  }
  
  async function saveHomeImage(imageUrl) {
    setSavingHomeImage(true);
    try {
      await setDoc(doc(db, 'settings', 'homeImage'), {
        url: imageUrl,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setHomeImage(imageUrl);
      setInfOk('Imagem da home salva com sucesso!');
      setTimeout(() => setInfOk(''), 2000);
    } catch (e) {
      console.error('Erro ao salvar imagem da home:', e);
      setInfError('Erro ao salvar imagem da home.');
      setTimeout(() => setInfError(''), 2000);
    } finally {
      setSavingHomeImage(false);
    }
  }
  
  async function saveBackgroundMusic(musicUrl) {
    setSavingMusic(true);
    setMusicError('');
    setMusicSuccess('');
    try {
      await setDoc(doc(db, 'settings', 'backgroundMusic'), {
        url: musicUrl,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setBackgroundMusic(musicUrl);
      setMusicSuccess('Música de fundo salva com sucesso!');
      setTimeout(() => setMusicSuccess(''), 3000);
    } catch (e) {
      console.error('Erro ao salvar música:', e);
      setMusicError('Erro ao salvar música.');
      setTimeout(() => setMusicError(''), 3000);
    } finally {
      setSavingMusic(false);
    }
  }
  
  async function saveHomeText(text) {
    setSavingHomeText(true);
    setTextError('');
    setTextSuccess('');
    try {
      await setDoc(doc(db, 'settings', 'homeText'), {
        text: text,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setHomeText(text);
      setTextSuccess('Texto da home salvo com sucesso!');
      setTimeout(() => setTextSuccess(''), 3000);
    } catch (e) {
      console.error('Erro ao salvar texto:', e);
      setTextError('Erro ao salvar texto.');
      setTimeout(() => setTextError(''), 3000);
    } finally {
      setSavingHomeText(false);
    }
  }

  async function loadInfluencers() {
    setInfLoadingList(true);
    try {
      const coll = collection(db, 'influencers');
      const q1 = query(coll, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q1);
      const base = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // contagem users por code
      const withCounts = await Promise.all(base.map(async (inf) => {
        try {
          const qUsers = query(collection(db, 'users'), where('referral.code', '==', inf.code || ''));
          const cnt = await getCountFromServer(qUsers);
          return { ...inf, usersCount: cnt.data().count || 0 };
        } catch {
          return { ...inf, usersCount: 0 };
        }
      }));
      setInfluencers(withCounts);
    } catch (e) {
      console.error(e);
      setInfluencers([]);
    } finally {
      setInfLoadingList(false);
    }
  }

  async function saveInfluencer(e) {
    e?.preventDefault?.();
    setInfError(''); setInfOk('');
    if (!infName.trim()) return setInfError('Informe o nome do influencer.');
    if (!infCode.trim()) return setInfError('Informe o código (ex.: IZABEL123).');
    if (!infSource.trim()) return setInfError('Informe o utm_source (ex.: izabel_pires_de_almeida).');
    if (!infPreviewIosLink) return setInfError('O link iOS precisa ser uma URL válida de apps.apple.com.');
    if (!new URL(infPreviewIosLink).searchParams.get('pt')) {
      return setInfError('O link iOS precisa conter o provider token (pt) do App Store Connect.');
    }

    const payload = {
      name: infName.trim(),
      code: infCode.trim(),
      utm_source: infSource.trim(),
      utm_campaign: infCampaign.trim() || null,
      appId: ANDROID_PACKAGE,
      playLink: infPreviewLink,
      appStoreLink: infPreviewIosLink,
      iosCampaignToken: infCode.trim().slice(0, 30),
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    setInfSaving(true);
    try {
      await setDoc(doc(db, 'influencers', infCode.trim()), payload, { merge: true });
      setInfOk('Link criado/atualizado com sucesso.');
      setInfName(''); setInfCode(''); setInfSource(''); setInfCampaign('');
      await loadInfluencers();
    } catch (e) {
      console.error(e);
      setInfError('Falha ao salvar. Tente novamente.');
    } finally {
      setInfSaving(false);
      setTimeout(() => { setInfOk(''); setInfError(''); }, 1800);
    }
  }

  function copy(text) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setInfOk('Copiado para a área de transferência.');
      setTimeout(() => setInfOk(''), 1500);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setInfOk('Copiado.'); } catch { }
      document.body.removeChild(ta);
      setTimeout(() => setInfOk(''), 1500);
    }
  }

  // período efetivo
  const { fromMs, toMs, labelPeriodo } = useMemo(() => {
    const now = new Date();
    if (preset === PRESETS.last7) {
      const from = startOfDay(new Date(now.getTime() - 7 * 86400000));
      const to = endOfDay(now);
      return { fromMs: from.getTime(), toMs: to.getTime(), labelPeriodo: 'Últimos 7 dias' };
    }
    if (preset === PRESETS.last30) {
      const from = startOfDay(new Date(now.getTime() - 30 * 86400000));
      const to = endOfDay(now);
      return { fromMs: from.getTime(), toMs: to.getTime(), labelPeriodo: 'Últimos 30 dias' };
    }
    if (preset === PRESETS.thisMonth) {
      const { first, last } = monthBounds(now);
      return { fromMs: first.getTime(), toMs: last.getTime(), labelPeriodo: 'Este mês' };
    }
    if (preset === PRESETS.custom && dateFrom && dateTo) {
      const from = startOfDay(new Date(dateFrom));
      const to = endOfDay(new Date(dateTo));
      return { fromMs: from.getTime(), toMs: to.getTime(), labelPeriodo: `${dateFrom} → ${dateTo}` };
    }
    return { fromMs: null, toMs: null, labelPeriodo: 'Todo período' };
  }, [preset, dateFrom, dateTo]);

  // carga inicial
  useEffect(() => { loadAll(); loadInfluencers(); loadStartImage(); loadStoreStats(); }, []);
  async function loadAll() {
    setLoading(true); setError('');
    try {
      await Promise.all([
        loadTotalUsers(),
        loadTopPacks(),
        loadRecentUsers(),
        loadAllUsersWithReferrerInfo(),
        loadPacksData(),
      ]);
    } catch (e) {
      console.error(e); setError('Falha ao carregar dados. Tente novamente.');
    } finally { setLoading(false); }
  }

  async function loadTotalUsers() {
    const coll = collection(db, 'users');
    const snapshot = await getCountFromServer(coll);
    setTotalUsers(snapshot.data().count || 0);
  }

  async function loadTopPacks() {
    const coll = collection(db, 'users');
    let q1 = query(coll, orderBy('updatedAt', 'desc'), limit(MAX_USERS_FOR_PACKS));
    let snap = await getDocs(q1);
    if (snap.empty) {
      q1 = query(coll, orderBy('email'), limit(MAX_USERS_FOR_PACKS));
      snap = await getDocs(q1);
    }
    const freq = new Map();
    snap.docs.forEach(d => {
      const arr = Array.isArray(d.data()?.purchasedPackages) ? d.data().purchasedPackages : [];
      arr.forEach(p => freq.set(p, (freq.get(p) || 0) + 1));
    });
    const arr = Array.from(freq.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
    setTopPacks(arr);
  }

  async function loadRecentUsers() {
    const coll = collection(db, 'users');
    let q1 = query(coll, orderBy('updatedAt', 'desc'), limit(7));
    let snap = await getDocs(q1);
    if (snap.empty) {
      q1 = query(coll, orderBy('email'), limit(7));
      snap = await getDocs(q1);
    }
    setRecentUsers(
      snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          email: data?.email || '—',
          updatedAt: data?.updatedAt?.toDate ? data.updatedAt.toDate() : null,
          purchasedPackages: Array.isArray(data?.purchasedPackages) ? data.purchasedPackages : []
        };
      })
    );
  }

  async function loadPacksData() {
    try {
      const coll = collection(db, 'packs');
      const snap = await getDocs(coll);
      const packs = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setPacksData(packs);
    } catch (e) {
      console.error('Erro ao carregar packs:', e);
      setPacksData([]);
    }
  }

  async function loadStoreStats() {
    setStoreLoading(true);
    setStoreError('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/getStoreStats?days=120`);
      const result = await response.json();
      if (!response.ok || result.success !== true || !Array.isArray(result.data)) {
        throw new Error(result.error || 'Resposta inválida ao consultar as estatísticas.');
      }
      setStoreStats(result.data.map(record => ({ ...record, id: record.date })));
    } catch (e) {
      console.error('Erro ao carregar storeStats:', e);
      setStoreError('Não foi possível carregar os dados das lojas. Tente atualizar novamente.');
    } finally {
      setStoreLoading(false);
    }
  }

  async function refreshStoreStats() {
    setStoreLoading(true);
    setStoreError('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/fetchStoreStats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: storePeriod === 'custom' ? 90 : parseInt(storePeriod, 10) || 30 }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setStoreError(data.error || 'Erro ao buscar dados das lojas.');
      } else {
        // Após atualizar os relatórios, recarrega o cache pela API.
        await loadStoreStats();
      }
    } catch (e) {
      console.error('Erro ao chamar fetchStoreStats:', e);
      setStoreError('Não foi possível conectar à Cloud Function.');
    } finally {
      setStoreLoading(false);
    }
  }

  async function loadAllUsersWithReferrerInfo() {
    const coll = collection(db, 'users');
    
    // Conta usuários com referrer
    try {
      const qCount = query(coll, where('referral.source', '==', 'install_referrer'));
      const countSnap = await getCountFromServer(qCount);
      setRefTotal(countSnap.data().count || 0);
    } catch (e) {
      console.warn('count referrer falhou:', e);
      setRefTotal(0);
    }

    // Busca TODOS os usuários - sem ordenação para evitar problemas com índices
    let snap;
    try {
      // Tenta buscar todos sem ordenação (mais garantido de funcionar)
      const q1 = query(coll);
      snap = await getDocs(q1);
      console.log('✅ Usuários carregados:', snap.docs.length);
    } catch (e) {
      console.error('Erro ao carregar usuários:', e);
      snap = { docs: [] };
    }

    const rows = snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        email: data?.email || '',
        updatedAt: data?.updatedAt?.toDate ? data.updatedAt.toDate() : null,
        referral: data?.referral || null,
        fcmToken: data?.fcmToken || null, // ✅ Adicionar token FCM
      };
    });

    setRefDocs(rows);
  }

  // filtros na base refDocs
  const refFiltered = useMemo(() => {
    const s = (x) => String(x || '').toLowerCase();
    const fs = s(fSource), fc = s(fCode), fcam = s(fCampaign);
    const searchTerm = s(userSearch);
    
    let filtered = refDocs.filter(({ email, updatedAt, referral }) => {
      // Filtro de busca em tempo real (email ou data)
      if (searchTerm) {
        const emailMatch = s(email).includes(searchTerm);
        const dateStr = updatedAt ? updatedAt.toLocaleString('pt-BR').toLowerCase() : '';
        const dateMatch = dateStr.includes(searchTerm);
        if (!emailMatch && !dateMatch) return false;
      }
      
      // Se "Apenas com referrer" está ATIVADO
      if (showOnlyReferrer) {
        // Deve ter referrer do tipo install_referrer
        if (!referral || referral?.source !== 'install_referrer') return false;
        
        // Aplica filtros de período e dimensão
        const savedAt = Number(referral.savedAt) || null;
        if (fromMs && savedAt && savedAt < fromMs) return false;
        if (toMs && savedAt && savedAt > toMs) return false;
        const parsed = referral.parsed || {};
        const src = s(parsed.utm_source), code = s(parsed.code), camp = s(parsed.utm_campaign);
        if (fs && !src.includes(fs)) return false;
        if (fc && !code.includes(fc)) return false;
        if (fcam && !camp.includes(fcam)) return false;
        
        return true;
      }
      
      // Se "Apenas com referrer" está DESATIVADO, mostra TODOS
      // Mas se tiver filtros específicos de referrer ativos, só mostra quem tem referrer
      if (fs || fc || fcam || fromMs || toMs) {
        // Tem filtros de referrer, então só mostra usuários com referrer que atendem os critérios
        if (!referral || referral?.source !== 'install_referrer') return false;
        
        const savedAt = Number(referral.savedAt) || null;
        if (fromMs && savedAt && savedAt < fromMs) return false;
        if (toMs && savedAt && savedAt > toMs) return false;
        const parsed = referral.parsed || {};
        const src = s(parsed.utm_source), code = s(parsed.code), camp = s(parsed.utm_campaign);
        if (fs && !src.includes(fs)) return false;
        if (fc && !code.includes(fc)) return false;
        if (fcam && !camp.includes(fcam)) return false;
        
        return true;
      }
      
      // Sem filtros e checkbox desativado: mostra TODOS
      return true;
    });
    
    // Ordenação
    filtered.sort((a, b) => {
      if (sortBy === 'updatedAt_desc') {
        const dateA = a.updatedAt || new Date(0);
        const dateB = b.updatedAt || new Date(0);
        return dateB - dateA;
      }
      if (sortBy === 'updatedAt_asc') {
        const dateA = a.updatedAt || new Date(0);
        const dateB = b.updatedAt || new Date(0);
        return dateA - dateB;
      }
      if (sortBy === 'email_asc') {
        return (a.email || '').localeCompare(b.email || '');
      }
      if (sortBy === 'email_desc') {
        return (b.email || '').localeCompare(a.email || '');
      }
      return 0;
    });
    
    return filtered;
  }, [refDocs, fromMs, toMs, fSource, fCode, fCampaign, showOnlyReferrer, sortBy, userSearch]);

  // rankings pós-filtro
  const { topSources, topCodes, topCampaigns } = useMemo(() => {
    const by = (key) => {
      const freq = new Map();
      refFiltered.forEach(r => {
        const v = String(r?.referral?.parsed?.[key] || '').trim();
        if (!v) return;
        freq.set(v, (freq.get(v) || 0) + 1);
      });
      return Array.from(freq.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    };
    return {
      topSources: by('utm_source'),
      topCodes: by('code'),
      topCampaigns: by('utm_campaign'),
    };
  }, [refFiltered]);

  // Dados para gráficos animados
  const chartDataByDay = useMemo(() => {
    // Agrupa usuários por dia — usa updatedAt de todos os usuários carregados
    const dayMap = new Map();

    refDocs.forEach(r => {
      // Preferir referral.savedAt, cair para updatedAt
      let date = null;
      if (r.referral?.savedAt) {
        date = new Date(Number(r.referral.savedAt));
      } else if (r.updatedAt) {
        date = r.updatedAt instanceof Date ? r.updatedAt : new Date(r.updatedAt);
      }
      if (!date || isNaN(date.getTime())) return;

      const dateKey = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      dayMap.set(dateKey, (dayMap.get(dateKey) || 0) + 1);
    });

    return Array.from(dayMap.entries())
      .map(([date, usuarios]) => ({ date, usuarios }))
      .sort((a, b) => {
        const [dA, mA] = a.date.split('/').map(Number);
        const [dB, mB] = b.date.split('/').map(Number);
        return mA !== mB ? mA - mB : dA - dB;
      })
      .slice(-30); // últimos 30 dias
  }, [refDocs]);

  const chartDataSources = useMemo(() => {
    return topSources.slice(0, 8).map(s => ({
      name: s.name.length > 15 ? s.name.substring(0, 15) + '...' : s.name,
      captacoes: s.count
    }));
  }, [topSources]);

  const chartDataSales = useMemo(() => {
    if (!topPacks.length) return [];

    return topPacks.slice(0, 8).map(pack => {
      const packInfo = packsData.find(p =>
        p.title === pack.name || p.id === pack.name
      );
      const priceStr = packInfo?.price || 'R$ 4,99';
      const priceNum = parseFloat(
        priceStr.replace('R$', '').replace(/\./g, '').replace(',', '.').trim()
      ) || 4.99;
      const revenue = pack.count * priceNum;

      return {
        name: pack.name.length > 15 ? pack.name.substring(0, 15) + '...' : pack.name,
        vendas: pack.count,
        receita: parseFloat(revenue.toFixed(2))
      };
    });
  }, [topPacks, packsData]);

  // Filtra storeStats pelo período selecionado
  const storeStatsFilt = useMemo(() => {
    if (!storeStats.length) return [];
    const now = new Date();
    let from, to;
    if (storePeriod === 'custom') {
      from = storeFrom ? new Date(storeFrom) : null;
      to = storeTo ? new Date(storeTo + 'T23:59:59') : null;
    } else {
      const days = parseInt(storePeriod, 10) || 30;
      from = new Date(now); from.setDate(from.getDate() - days);
      to = now;
    }
    return storeStats.filter(r => {
      if (!r.date) return true;
      const d = new Date(r.date);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [storeStats, storePeriod, storeFrom, storeTo]);

  // Dados das lojas para gráficos
  const storeInstallsByDay = useMemo(() => {
    if (!storeStatsFilt.length) return [];
    // Cada doc do Firestore tem android.daily = { 'YYYY-MM-DD': { installs, sales, revenue } }
    // Expande os diários de todos os docs em um mapa por data
    const dayMap = new Map();
    storeStatsFilt.forEach((dayRecord) => {
      ['android', 'ios'].forEach(p => {
        Object.entries(dayRecord[p]?.daily || {}).forEach(([dateKey, v]) => {
          if (!dayMap.has(dateKey)) dayMap.set(dateKey, { date: dateKey, android: 0, ios: 0 });
          dayMap.get(dateKey)[p] += v.installs || 0;
        });
      });
    });
    const label = (iso) => { const [, m, d] = iso.split('-'); return `${d}/${m}`; };
    return Array.from(dayMap.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => ({ ...r, date: label(r.date) }));
  }, [storeStatsFilt]);

  const storeSalesByDay = useMemo(() => {
    if (!storeStatsFilt.length) return [];
    const dayMap = new Map();
    storeStatsFilt.forEach((dayRecord) => {
      const day = dayRecord.date || dayRecord.id;
      if (!day) return;
      if (!dayMap.has(day)) dayMap.set(day, { date: day, android: 0, ios: 0 });
      const entry = dayMap.get(day);
      ['android','ios'].forEach(p => {
        Object.values(dayRecord[p]?.daily || {}).forEach(v => {
          entry[p] += v.revenue || 0;
        });
      });
    });
    const label = (iso) => { const [, m, d] = iso.split('-'); return `${d}/${m}`; };
    return Array.from(dayMap.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => ({ ...r, date: label(r.date), android: parseFloat(r.android.toFixed(2)), ios: parseFloat(r.ios.toFixed(2)) }));
  }, [storeStatsFilt]);

  const storeTotals = useMemo(() => {
    const zero = { android: { installs: 0, revenue: 0 }, ios: { installs: 0, revenue: 0 }, days: 0 };
    if (!storeStatsFilt.length) return zero;
    let aI = 0, iI = 0, aR = 0, iR = 0;
    const allDays = new Set();
    storeStatsFilt.forEach((r) => {
      // Usa apenas o daily — evita duplicar com totalInstalls
      Object.entries(r.android?.daily || {}).forEach(([day, d]) => { allDays.add(day); aI += d.installs || 0; aR += d.revenue || 0; });
      Object.entries(r.ios?.daily || {}).forEach(([day, d]) => { allDays.add(day); iI += d.installs || 0; iR += d.revenue || 0; });
    });
    return {
      android: { installs: aI, revenue: parseFloat(aR.toFixed(2)) },
      ios: { installs: iI, revenue: parseFloat(iR.toFixed(2)) },
      days: allDays.size,
    };
  }, [storeStatsFilt]);

  // paginação e seleção da tabela
  const totalRows = refFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const rowsPage = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return refFiltered.slice(start, start + pageSize);
  }, [refFiltered, pageSafe, pageSize]);

  useEffect(() => { setPage(1); }, [fSource, fCode, fCampaign, fromMs, toMs, preset, showOnlyReferrer, sortBy, userSearch]);

  function toggleRow(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllCurrentPage() {
    const ids = rowsPage.map(r => r.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) { ids.forEach(id => next.delete(id)); }
      else { ids.forEach(id => next.add(id)); }
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }

  function handlePresetChange(next) {
    setPreset(next);
    if (next !== PRESETS.custom) { setDateFrom(''); setDateTo(''); }
  }

  // helpers UI
  const chip = (label, onClick) => (
    <button
      key={label}
      onClick={onClick}
      style={styles.chip}
      title={`Aplicar filtro: ${label}`}
    >
      {label}
    </button>
  );

  // ==================================
  // RENDER
  // ==================================
  return (
    <div style={{ ...styles.page, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <header
        style={{
          ...styles.header,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 12 : 0
        }}
      >
        <div style={{ ...styles.brand, justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <div style={styles.logoCircle}>TA</div>
          <div>
            <div style={{ ...styles.title, fontSize: isMobile ? 16 : 18 }}>TalkAboutIt — Admin</div>
            <div style={styles.subtitle}>Painel de controle</div>
          </div>
        </div>
        <div
          style={{
            ...styles.headerActions,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 8 : 10
          }}
        >
          <button
            onClick={() => setShowNotificationModal(true)}
            style={{
              ...styles.button,
              ...styles.primary,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <FiBell style={styles.icon} /> Notificações Push
          </button>
          <Link
            to="/admin/packs"
            style={{
              ...styles.button,
              ...styles.secondary,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <FiSettings style={styles.icon} /> Gerenciar Packs
          </Link>
          <Link
            to="/admin/pacotes"
            style={{
              ...styles.button,
              ...styles.secondary,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <FiPlusCircle style={styles.icon} /> Adicionar ao Usuário
          </Link>
          <button
            onClick={loadAll}
            style={{
              ...styles.button,
              ...styles.ghost,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <FiRefreshCw style={styles.icon} /> Atualizar
          </button>
          <button
            onClick={logout}
            style={{
              ...styles.button,
              ...styles.ghost,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              color: '#dc2626'
            }}
            title="Sair"
          >
            <FiLogOut style={styles.icon} />
          </button>
        </div>
      </header>

      {error && <div style={styles.error}>{error}</div>}

      {/* Cards principais */}
      <section
        style={{
          ...styles.cardsGrid,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))'
        }}
      >
        <Card
          icon={<FiUsers size={22} />}
          title="Usuários Totais"
          value={loading ? '—' : totalUsers.toLocaleString('pt-BR')}
          hint="Contagem total no Firestore"
        />
        <Card
          icon={<FiPackage size={22} />}
          title="Pack mais popular"
          value={loading ? '—' : (topPacks[0]?.name || '—')}
          hint={loading ? '' : `${topPacks[0]?.count || 0} usuários`}
        />
        <Card
          icon={<FiTrendingUp size={22} />}
          title="Top 3 Packs"
          value={loading ? '—' : topPacks.slice(0, 3).map(p => p.name).join(', ') || '—'}
          hint={`Amostra de até ${MAX_USERS_FOR_PACKS} usuários`}
        />
        <Card
          icon={<FiLink2 size={22} />}
          title="Instalações via Referrer"
          value={loading ? '—' : refTotal.toLocaleString('pt-BR')}
          hint='referral.source = "install_referrer"'
        />
      </section>

      {/* ==== SEÇÃO: Imagens de Background ==== */}
      <section style={styles.infBlock}>
        <div style={styles.searchHeader}>
          <div style={styles.blockTitle}><FiImage /> &nbsp;Imagens de Background</div>
          <div style={styles.blockHint}>
            Configure as imagens de fundo para login e home do aplicativo
          </div>
        </div>

        {(savingLoginImage || savingHomeImage) && (
          <div style={styles.alertInfo}>Salvando imagem...</div>
        )}

        {/* Grid com duas seções lado a lado */}
        <div style={styles.imagesGrid}>
          {/* Imagem de Login */}
          <div style={styles.imageCard}>
            <ImageSelector
              currentImage={loginImage}
              onImageSelect={saveLoginImage}
              title="Login"
              showGallery={true}
              accept="image/*"
              fileType="imagem"
              cardTitle="🔐 Tela de Login"
              cardHint="Imagem de fundo da tela de login"
            />
          </div>
          
          {/* Imagem de Home */}
          <div style={styles.imageCard}>
            <ImageSelector
              currentImage={homeImage}
              onImageSelect={saveHomeImage}
              title="Home"
              showGallery={true}
              accept="image/*"
              fileType="imagem"
              cardTitle="🏠 Tela de Home"
              cardHint="Imagem de fundo da tela inicial (Início)"
            />
          </div>
        </div>
      </section>

      {/* ==== SEÇÃO: Música e Texto ==== */}
      <section style={styles.infBlock}>
        <div style={styles.searchHeader}>
          <div style={styles.blockTitle}>🎵 Customização de Conteúdo</div>
          <div style={styles.blockHint}>
            Configure música de fundo e texto da tela inicial
          </div>
        </div>

        {/* Mensagens de sucesso/erro */}
        {(musicSuccess || musicError || textSuccess || textError) && (
          <div style={{ marginBottom: 12 }}>
            {musicSuccess && <div style={styles.alertOk}>{musicSuccess}</div>}
            {musicError && <div style={styles.alertError}>{musicError}</div>}
            {textSuccess && <div style={styles.alertOk}>{textSuccess}</div>}
            {textError && <div style={styles.alertError}>{textError}</div>}
          </div>
        )}

        {(savingMusic || savingHomeText) && (
          <div style={styles.alertInfo}>Salvando...</div>
        )}

        {/* Grid com duas seções lado a lado */}
        <div style={styles.imagesGrid}>
          {/* Música de Fundo */}
          <div style={styles.imageCard}>
            <MusicUploader
              currentMusic={backgroundMusic}
              onMusicSelect={saveBackgroundMusic}
              title="BackgroundMusic"
              cardTitle="🎵 Música de Fundo"
              cardHint="Música que toca na tela Início (padrão se nenhuma selecionada)"
            />
          </div>
          
          {/* Texto da Home */}
          <div style={styles.imageCard}>
            <TextEditor
              currentText={homeText}
              onTextSave={saveHomeText}
              placeholder="Digite o texto customizado..."
              label="Frase de Efeito"
              maxLength={100}
              cardTitle="📝 Texto da Home"
              cardHint='Substitui "Desconecte-se para se reconectar"'
            />
          </div>
        </div>
      </section>

      {/* Seção: Dados das Lojas (Google Play / App Store) */}
      <section style={styles.section}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={styles.blockTitle}>📱 Lojas (Google Play / App Store)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a
              href={`https://us-central1-talk-about-it-789ff.cloudfunctions.net/diagStoreApi`}
              target="_blank" rel="noreferrer"
              style={{ ...styles.btnRefresh, background: '#6b7280', border: '1px solid #6b7280', fontSize: 12, padding: '7px 12px' }}
            >
              🔍 Diagnóstico
            </a>
            <button onClick={refreshStoreStats} disabled={storeLoading} style={styles.btnRefresh}>
              <FiRefreshCw size={13} />
              {storeLoading ? 'Buscando...' : 'Buscar dados'}
            </button>
          </div>
        </div>

        {/* Filtros de período */}
        <div style={styles.storeFilterRow}>
          {[['7','7 dias'],['15','15 dias'],['30','30 dias'],['90','90 dias'],['730','Tudo'],['custom','Personalizado']].map(([val, lbl]) => (
            <button key={val} onClick={() => setStorePeriod(val)}
              style={{ ...styles.storeFilterBtn, ...(storePeriod === val ? styles.storeFilterBtnActive : {}) }}>
              {lbl}
            </button>
          ))}
        </div>

        {storePeriod === 'custom' && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: '#555' }}>De:</label>
            <input type="date" value={storeFrom} onChange={e => setStoreFrom(e.target.value)}
              style={{ border: '1px solid #ddd', borderRadius: 8, padding: '6px 10px', fontSize: 13 }} />
            <label style={{ fontSize: 13, color: '#555' }}>Até:</label>
            <input type="date" value={storeTo} onChange={e => setStoreTo(e.target.value)}
              style={{ border: '1px solid #ddd', borderRadius: 8, padding: '6px 10px', fontSize: 13 }} />
          </div>
        )}

        {storeError && (
          <div style={{ ...styles.alertError, marginBottom: 12 }}>
            ⚠️ {storeError} — <a href="https://us-central1-talk-about-it-789ff.cloudfunctions.net/diagStoreApi" target="_blank" rel="noreferrer" style={{ color: '#b91c1c' }}>ver diagnóstico</a>
          </div>
        )}
        {storeLoading && <div style={styles.loading}>Buscando dados das lojas...</div>}

        {!storeLoading && storeStats.length === 0 && !storeError && (
          <div style={{ padding: '32px 24px', background: '#f9f9fb', borderRadius: 14, border: '1px dashed #ddd', textAlign: 'center', lineHeight: 2 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
            <strong>Nenhum dado encontrado no Firestore.</strong><br />
            <span style={{ fontSize: 13, color: '#888' }}>
              1. Configure as credenciais em <code>settings/storeApi</code><br />
              2. Clique <strong>Buscar dados</strong> para chamar as APIs e salvar no Firestore<br />
              3. Use <strong>🔍 Diagnóstico</strong> para verificar se as credenciais estão corretas
            </span>
          </div>
        )}

        {storeStats.length > 0 && (
          <>
            {/* Grid 2x2: 4 cards de métricas */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
              gap: 12,
              marginBottom: 20,
            }}>
              {/* Card 1: Downloads Android */}
              <div style={styles.storeMetricCard}>
                <div style={{ ...styles.storeMetricIcon, background: '#eff6ff', color: '#3b82f6' }}>🤖</div>
                <div style={styles.storeMetricVal}>{storeTotals.android.installs.toLocaleString('pt-BR')}</div>
                <div style={styles.storeMetricLabel}>Downloads Android</div>
                <div style={{ ...styles.storeMetricSub, color: '#3b82f6' }}>Google Play</div>
              </div>
              {/* Card 2: Downloads iOS */}
              <div style={styles.storeMetricCard}>
                <div style={{ ...styles.storeMetricIcon, background: '#f0fdf4', color: '#10b981' }}>🍎</div>
                <div style={styles.storeMetricVal}>{storeTotals.ios.installs.toLocaleString('pt-BR')}</div>
                <div style={styles.storeMetricLabel}>Downloads iOS</div>
                <div style={{ ...styles.storeMetricSub, color: '#10b981' }}>App Store</div>
              </div>
              {/* Card 3: Receita Android */}
              <div style={styles.storeMetricCard}>
                <div style={{ ...styles.storeMetricIcon, background: '#fefce8', color: '#f59e0b' }}>💰</div>
                <div style={{ ...styles.storeMetricVal, fontSize: 18 }}>
                  R$ {storeTotals.android.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div style={styles.storeMetricLabel}>Receita Android</div>
                <div style={{ ...styles.storeMetricSub, color: '#f59e0b' }}>Google Play</div>
              </div>
              {/* Card 4: Receita iOS */}
              <div style={styles.storeMetricCard}>
                <div style={{ ...styles.storeMetricIcon, background: '#fdf4ff', color: '#a855f7' }}>💎</div>
                <div style={{ ...styles.storeMetricVal, fontSize: 18 }}>
                  R$ {storeTotals.ios.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div style={styles.storeMetricLabel}>Receita iOS</div>
                <div style={{ ...styles.storeMetricSub, color: '#a855f7' }}>App Store</div>
              </div>
            </div>

            {/* Barra de totais */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #1f6feb 100%)',
              borderRadius: 14, padding: '14px 20px', marginBottom: 20,
              display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 12,
            }}>
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>
                  {(storeTotals.android.installs + storeTotals.ios.installs).toLocaleString('pt-BR')}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>total downloads</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>
                  R$ {(storeTotals.android.revenue + storeTotals.ios.revenue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>receita total</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{storeTotals.days}</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>dias com dados</div>
              </div>
            </div>

            {/* Grid 2x2: gráficos */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 16,
            }}>
              {/* Gráfico 1: Downloads Android */}
              <div style={styles.analyticCard}>
                <div style={styles.analyticCardHeader}>
                  <div style={styles.analyticCardTitle}>🤖 Downloads Android</div>
                  <div style={styles.analyticCardSub}>{storeInstallsByDay.length} dias</div>
                </div>
                {storeInstallsByDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={storeInstallsByDay} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="date" stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} interval={Math.floor(storeInstallsByDay.length / 5)} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                      <Tooltip contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, fontSize: 12 }}
                        formatter={v => [v.toLocaleString('pt-BR'), 'Downloads']} labelFormatter={l => `📅 ${l}`} />
                      <Line type="monotone" dataKey="android" stroke="#3b82f6" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} animationDuration={700} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div style={styles.storeEmptyChart}>Sem dados no período</div>}
              </div>

              {/* Gráfico 2: Downloads iOS */}
              <div style={styles.analyticCard}>
                <div style={styles.analyticCardHeader}>
                  <div style={styles.analyticCardTitle}>🍎 Downloads iOS</div>
                  <div style={styles.analyticCardSub}>{storeInstallsByDay.length} dias</div>
                </div>
                {storeInstallsByDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={storeInstallsByDay} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="date" stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} interval={Math.floor(storeInstallsByDay.length / 5)} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                      <Tooltip contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, fontSize: 12 }}
                        formatter={v => [v.toLocaleString('pt-BR'), 'Downloads']} labelFormatter={l => `📅 ${l}`} />
                      <Line type="monotone" dataKey="ios" stroke="#10b981" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} animationDuration={700} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div style={styles.storeEmptyChart}>Sem dados no período</div>}
              </div>

              {/* Gráfico 3: Receita Android */}
              <div style={styles.analyticCard}>
                <div style={styles.analyticCardHeader}>
                  <div style={styles.analyticCardTitle}>💰 Receita Android (R$)</div>
                  <div style={styles.analyticCardSub}>{storeSalesByDay.length} dias</div>
                </div>
                {storeSalesByDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={storeSalesByDay} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="date" stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} interval={Math.floor(storeSalesByDay.length / 5)} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} width={45} tickFormatter={v => `R$${v}`} />
                      <Tooltip contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, fontSize: 12 }}
                        formatter={v => [`R$ ${Number(v).toFixed(2)}`, 'Receita']} labelFormatter={l => `📅 ${l}`} />
                      <Bar dataKey="android" fill="#3b82f6" radius={[4,4,0,0]} barSize={isMobile ? 8 : 14} animationDuration={700} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={styles.storeEmptyChart}>Sem dados no período</div>}
              </div>

              {/* Gráfico 4: Receita iOS */}
              <div style={styles.analyticCard}>
                <div style={styles.analyticCardHeader}>
                  <div style={styles.analyticCardTitle}>💎 Receita iOS (R$)</div>
                  <div style={styles.analyticCardSub}>{storeSalesByDay.length} dias</div>
                </div>
                {storeSalesByDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={storeSalesByDay} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="date" stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} interval={Math.floor(storeSalesByDay.length / 5)} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#888' }} tickLine={false} axisLine={false} width={45} tickFormatter={v => `R$${v}`} />
                      <Tooltip contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, fontSize: 12 }}
                        formatter={v => [`R$ ${Number(v).toFixed(2)}`, 'Receita']} labelFormatter={l => `📅 ${l}`} />
                      <Bar dataKey="ios" fill="#a855f7" radius={[4,4,0,0]} barSize={isMobile ? 8 : 14} animationDuration={700} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={styles.storeEmptyChart}>Sem dados no período</div>}
              </div>
            </div>
          </>
        )}
        <br/>
      </section>

      {/* ==== NOVA SEÇÃO: Gerar/gerenciar links de influencers ==== */}
      <section style={styles.infBlock}>
        <div style={styles.searchHeader}>
          <div style={styles.blockTitle}><FiLink2 /> &nbsp;Gerador de Links — Influencers</div>
        </div>
        <div style={{ ...styles.muted, marginBottom: 12 }}>
          O Android associa o código ao cadastro pelo Install Referrer. No iOS, downloads, vendas e uso são
          atribuídos à campanha de forma agregada no App Store Connect; a Apple não expõe o usuário individual.
        </div>

        {(infError || infOk) && (
          <div style={{ marginBottom: 8 }}>
            {infError && <div style={styles.alertError}>{infError}</div>}
            {infOk && <div style={styles.alertOk}>{infOk}</div>}
          </div>
        )}

        {/* Form */}
        <form onSubmit={saveInfluencer} style={{ display: 'grid', gap: 10 }}>
          <div style={styles.row}>
            <label style={{ ...styles.label, width: 140 }}>Nome</label>
            <input
              style={{ ...styles.input, flex: '1 1 300px' }}
              placeholder="Ex.: Lucas Oliveira Angelo"
              value={infName}
              onChange={(e) => setInfName(e.target.value)}
            />
          </div>
          <div style={styles.row}>
            <label style={{ ...styles.label, width: 140 }}>Código (code)</label>
            <input
              style={{ ...styles.input, flex: '1 1 220px' }}
              placeholder="Ex.: lucas123"
              value={infCode}
              onChange={(e) => setInfCode(e.target.value.toUpperCase())}
            />
          </div>
          <div style={styles.row}>
            <label style={{ ...styles.label, width: 140 }}>utm_source</label>
            <input
              style={{ ...styles.input, flex: '1 1 260px' }}
              placeholder="Ex.: lucasoliveiraangelo"
              value={infSource}
              onChange={(e) => setInfSource(slugify(e.target.value))}
            />
          </div>
          <div style={styles.row}>
            <label style={{ ...styles.label, width: 140 }}>utm_campaign</label>
            <input
              style={{ ...styles.input, flex: '1 1 220px' }}
              placeholder="Ex.: campanha2025"
              value={infCampaign}
              onChange={(e) => setInfCampaign(slugify(e.target.value))}
            />
          </div>

          <div style={{ ...styles.row, alignItems: 'flex-start' }}>
            <label style={{ ...styles.label, width: 140 }}>Links gerados</label>
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={styles.muted}>Android — Google Play</div>
              <code style={styles.code}>{infPreviewLink}</code>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" style={styles.btnSmLight} onClick={() => copy(infPreviewLink)}>Copiar Android</button>
              </div>
              <div style={{ ...styles.muted, marginTop: 6 }}>iOS — App Store</div>
              <code style={styles.code}>{infPreviewIosLink}</code>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" disabled={!infPreviewIosLink} style={styles.btnSmLight} onClick={() => copy(infPreviewIosLink)}>Copiar iOS</button>
              </div>
            </div>
          </div>

          <div>
            <button type="submit" style={styles.btnSm} disabled={infSaving}>
              {infSaving ? 'Salvando...' : 'Salvar links do influencer'}
            </button>
          </div>
        </form>

        {/* Lista de influencers */}
        <div style={{ marginTop: 16 }}>
          <div style={styles.blockTitle}>Influencers cadastrados</div>
          {infLoadingList ? (
            <div style={styles.muted}>Carregando...</div>
          ) : influencers.length === 0 ? (
            <div style={styles.muted}>Nenhum influencer cadastrado ainda.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Nome</th>
                    <th style={styles.th}>code</th>
                    <th style={styles.th}>utm_source</th>
                    <th style={styles.th}>utm_campaign</th>
                    <th style={styles.th}>Usuários captados</th>
                    <th style={styles.th}>Links</th>
                  </tr>
                </thead>
                <tbody>
                  {influencers.map((inf) => (
                    <tr key={inf.id}>
                      <td style={styles.td}>{inf.name || '—'}</td>
                      <td style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{inf.code || '—'}</td>
                      <td style={styles.tdSmall}>{inf.utm_source || '—'}</td>
                      <td style={styles.tdSmall}>{inf.utm_campaign || '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}><strong>{inf.usersCount ?? 0}</strong></td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" style={styles.btnSmLight} onClick={() => copy(inf.playLink)}>Copiar Android</button>
                          {inf.appStoreLink ? (
                            <button type="button" style={styles.btnSmLight} onClick={() => copy(inf.appStoreLink)}>Copiar iOS</button>
                          ) : (
                            <span style={styles.muted}>iOS não configurado</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Filtros (Referrer Analytics) */}
      <section style={styles.filtersBlock}>
        <div style={styles.filtersHeader}>
          <div style={styles.blockTitle}><FiFilter /> &nbsp;Referrer — Filtros de análise</div>
          <div style={styles.blockHint}>
            Estes filtros afetam os rankings e a tabela de usuários abaixo. O período usa <code>referral.savedAt</code> (ms).
          </div>
        </div>

        {/* Linha 1: Presets */}
        <div
          style={{
            ...styles.row,
            alignItems: 'stretch',
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <label style={{ ...styles.label, width: isMobile ? '100%' : 90 }}>Período:</label>
          <div style={{ ...styles.presets, width: '100%' }}>
            {[
              { id: PRESETS.last7, label: 'Últimos 7 dias' },
              { id: PRESETS.last30, label: 'Últimos 30 dias' },
              { id: PRESETS.thisMonth, label: 'Este mês' },
              { id: PRESETS.all, label: 'Tudo' },
              { id: PRESETS.custom, label: 'Personalizado' },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => handlePresetChange(p.id)}
                style={{
                  ...styles.presetBtn,
                  ...(preset === p.id ? styles.presetBtnActive : {}),
                  flex: isMobile ? '1 1 auto' : '0 0 auto'
                }}
              >
                {p.label}
              </button>
            ))}
            <span style={styles.periodoLabel}>Atual: <strong>{labelPeriodo}</strong></span>
          </div>
        </div>

        {/* Linha 2: Datas custom */}
        {preset === PRESETS.custom && (
          <div
            style={{
              ...styles.row,
              alignItems: 'stretch',
              flexDirection: isMobile ? 'column' : 'row'
            }}
          >
            <label style={{ ...styles.label, width: isMobile ? '100%' : 90 }}>Data (de/até):</label>
            <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap' }}>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...styles.dateInput, flex: '1 1 160px' }} />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...styles.dateInput, flex: '1 1 160px' }} />
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={styles.button}>Limpar</button>
            </div>
          </div>
        )}

        {/* Linha 3: Dimensões */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600, marginBottom: 8 }}>Dimensões:</label>
          <div
            style={{
              ...styles.dimensions,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))'
            }}
          >
            <div style={styles.dimCol}>
              <div style={styles.dimLabel}>Influencer (utm_source)</div>
              <input
                placeholder="ex.: ana-beatriz-xavier"
                value={fSource}
                onChange={(e) => setFSource(e.target.value)}
                style={styles.input}
              />
              <div style={styles.dimHelp}>Filtro por <em>contém</em> (case-insensitive)</div>
              {topSources.length > 0 && (
                <div style={styles.chipsRow}>
                  {topSources.slice(0, 6).map(s => chip(s.name, () => setFSource(s.name)))}
                </div>
              )}
            </div>
            <div style={styles.dimCol}>
              <div style={styles.dimLabel}>Código (code)</div>
              <input
                placeholder="ex.: ANABEATRIZ2025"
                value={fCode}
                onChange={(e) => setFCode(e.target.value)}
                style={styles.input}
              />
              <div style={styles.dimHelp}>Ex.: <code>code=ANABEATRIZ2025</code></div>
              {topCodes.length > 0 && (
                <div style={styles.chipsRow}>
                  {topCodes.slice(0, 6).map(s => chip(s.name, () => setFCode(s.name)))}
                </div>
              )}
            </div>
            <div style={styles.dimCol}>
              <div style={styles.dimLabel}>Campanha (utm_campaign)</div>
              <input
                placeholder="ex.: campanha-2025"
                value={fCampaign}
                onChange={(e) => setFCampaign(e.target.value)}
                style={styles.input}
              />
              <div style={styles.dimHelp}>Use pedaços do nome (contém)</div>
              {topCampaigns.length > 0 && (
                <div style={styles.chipsRow}>
                  {topCampaigns.slice(0, 6).map(s => chip(s.name, () => setFCampaign(s.name)))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Rankings após filtros */}
      <section
        style={{
          ...styles.split,
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr'
        }}
      >
        <div style={styles.splitCol}>
          <div style={styles.blockTitle}>Top Influencers (utm_source)</div>
          <ul style={styles.topList}>
            {topSources.map(r => (
              <li key={r.name} style={styles.topItem}>
                <span style={styles.badge}>{r.count}</span>
                <span>{r.name}</span>
              </li>
            ))}
            {topSources.length === 0 && <li style={styles.empty}>Sem dados para os filtros atuais.</li>}
          </ul>
        </div>

        <div style={styles.splitCol}>
          <div style={styles.blockTitle}>Top Códigos (code)</div>
          <ul style={styles.topList}>
            {topCodes.map(r => (
              <li key={r.name} style={styles.topItem}>
                <span style={styles.badge}>{r.count}</span>
                <span>{r.name}</span>
              </li>
            ))}
            {topCodes.length === 0 && <li style={styles.empty}>Sem dados para os filtros atuais.</li>}
          </ul>

          <div style={{ marginTop: 14 }}>
            <div style={styles.blockTitle}>Top Campanhas (utm_campaign)</div>
            <ul style={styles.topList}>
              {topCampaigns.map(r => (
                <li key={r.name} style={styles.topItem}>
                  <span style={styles.badge}>{r.count}</span>
                  <span>{r.name}</span>
                </li>
              ))}
              {topCampaigns.length === 0 && <li style={styles.empty}>Sem dados para os filtros atuais.</li>}
            </ul>
          </div>
        </div>
      </section>

      {/* Tabela de Usuários (com referrer) */}
      <section style={styles.refTableBlock}>
        <div style={styles.searchHeader}>
          <div style={styles.blockTitle}>Todos os Usuários Cadastrados</div>
          <div style={styles.blockHint}>
            {totalRows} resultado(s) • Total de {refDocs.length} usuários carregados
          </div>
        </div>
        
        {/* Campo de busca */}
        <div style={{ marginBottom: 12 }}>
          <div style={styles.searchInputWrap}>
            <FiSearch size={18} />
            <input
              placeholder="Buscar por email ou data..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={styles.searchInput}
            />
            {userSearch && (
              <button
                onClick={() => setUserSearch('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: '#999',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Controles de filtro e ordenação */}
        <div style={{
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '12px 0',
          borderBottom: '1px dashed #eee',
          marginBottom: 12
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showOnlyReferrer}
              onChange={(e) => setShowOnlyReferrer(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Apenas com referrer</span>
            <span style={{ fontSize: 12, color: '#777' }}>({refTotal})</span>
          </label>
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#444' }}>Ordenar:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              <option value="updatedAt_desc">Data (mais recente)</option>
              <option value="updatedAt_asc">Data (mais antigo)</option>
              <option value="email_asc">Email (A-Z)</option>
              <option value="email_desc">Email (Z-A)</option>
            </select>
          </div>
        </div>

        {/* Ações seleção */}
        <div style={styles.selBar}>
          <div style={styles.selInfo}>
            Selecionados: <strong>{selectedIds.size}</strong>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={clearSelection} style={styles.btnSmLight}>Limpar seleção</button>
            <button
              onClick={handleGerenciarPacks}
              style={{ ...styles.btnSm, textDecoration: 'none' }}
              disabled={selectedIds.size === 0}
            >
              Gerenciar packs dos selecionados
            </button>
          </div>
        </div>

        {/* Tabela desktop / Cards mobile */}
        {isMobile ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {rowsPage.map((r) => {
              const parsed = r.referral?.parsed || {};
              const checked = selectedIds.has(r.id);
              return (
                <div key={r.id} style={m.cardRow}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRow(r.id)}
                    />
                    <strong>{r.email || '—'}</strong>
                  </label>
                  <div style={m.rowLine}>
                    <strong>Atualizado:</strong>{' '}
                    {r.updatedAt ? r.updatedAt.toLocaleString('pt-BR') : '—'}
                  </div>
                  <div style={m.rowLine}><strong>utm_source:</strong> {parsed.utm_source || '—'}</div>
                  <div style={m.rowLine}><strong>code:</strong> {parsed.code || '—'}</div>
                  <div style={m.rowLine}><strong>utm_campaign:</strong> {parsed.utm_campaign || '—'}</div>
                  <div style={m.rowLine}>
                    <strong>captado em:</strong>{' '}
                    {r.referral?.savedAt ? new Date(r.referral.savedAt).toLocaleString('pt-BR') : '—'}
                  </div>
                </div>
              );
            })}
            {rowsPage.length === 0 && <div style={styles.empty}>Sem usuários para os filtros atuais.</div>}
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        onChange={toggleAllCurrentPage}
                        checked={rowsPage.length > 0 && rowsPage.every(r => selectedIds.has(r.id))}
                        aria-label="Selecionar página"
                      />
                      Selecionar
                    </label>
                  </th>
                  <th style={styles.th}>E-mail</th>
                  <th style={styles.th}>Atualizado em</th>
                  <th style={styles.th}>utm_source</th>
                  <th style={styles.th}>code</th>
                  <th style={styles.th}>utm_campaign</th>
                  <th style={styles.th}>captado em</th>
                </tr>
              </thead>
              <tbody>
                {rowsPage.map((r) => {
                  const parsed = r.referral?.parsed || {};
                  const checked = selectedIds.has(r.id);
                  return (
                    <tr key={r.id}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRow(r.id)}
                        />
                      </td>
                      <td style={styles.td}>{r.email || '—'}</td>
                      <td style={styles.tdSmall}>
                        {r.updatedAt ? r.updatedAt.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td style={styles.tdSmall}>{parsed.utm_source || '—'}</td>
                      <td style={styles.tdSmall}>{parsed.code || '—'}</td>
                      <td style={styles.tdSmall}>{parsed.utm_campaign || '—'}</td>
                      <td style={styles.tdSmall}>
                        {r.referral?.savedAt ? new Date(r.referral.savedAt).toLocaleString('pt-BR') : '—'}
                      </td>
                    </tr>
                  );
                })}
                {rowsPage.length === 0 && (
                  <tr><td colSpan={7} style={styles.empty}>Sem usuários para os filtros atuais.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        <div style={styles.pagination}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={pageSafe <= 1}
            style={styles.btnSmLight}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: 13, color: '#555' }}>
            Página <strong>{pageSafe}</strong> de <strong>{totalPages}</strong>
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={pageSafe >= totalPages}
            style={styles.btnSmLight}
          >
            Próxima →
          </button>
        </div>
      </section>

      {/* Últimos usuários + Top packs */}
      <section
        style={{
          ...styles.split,
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          marginBottom: 0
        }}
      >
        <div style={styles.splitCol}>
          <div style={styles.blockTitle}>Top Packs (estimativa)</div>
          <div style={styles.blockHint}>Base em amostra de até {MAX_USERS_FOR_PACKS} usuários</div>
          <ul style={styles.topList}>
            {topPacks.map((p) => (
              <li key={p.name} style={styles.topItem}>
                <span style={styles.badge}>{p.count}</span>
                <span>{p.name}</span>
              </li>
            ))}
            {topPacks.length === 0 && <li style={styles.empty}>Nenhum pack encontrado.</li>}
          </ul>
        </div>

        <div style={styles.splitCol}>
          <div style={styles.blockTitle}>Últimos usuários</div>
          <div style={styles.blockHint}>Atualizados recentemente</div>

          {isMobile ? (
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {recentUsers.map(u => (
                <div key={u.id} style={m.cardRow}>
                  <div style={m.rowLine}><strong>E-mail:</strong> {u.email}</div>
                  <div style={m.rowLine}>
                    <strong>Atualizado:</strong> {u.updatedAt ? u.updatedAt.toLocaleString('pt-BR') : '—'}
                  </div>
                  <div style={m.rowLine}>
                    <strong>Packs:</strong> {u.purchasedPackages.length ? u.purchasedPackages.join(', ') : '—'}
                  </div>
                </div>
              ))}
              {recentUsers.length === 0 && <div style={styles.empty}>Sem dados.</div>}
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>E-mail</th>
                    <th style={styles.th}>Atualizado</th>
                    <th style={styles.th}>Packs</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((u) => (
                    <tr key={u.id}>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.tdSmall}>
                        {u.updatedAt ? u.updatedAt.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td style={styles.tdSmall}>
                        {u.purchasedPackages.length ? u.purchasedPackages.join(', ') : '—'}
                      </td>
                    </tr>
                  ))}
                  {recentUsers.length === 0 && (
                    <tr><td colSpan={3} style={styles.empty}>Sem dados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Modal de Notificações */}
      {showNotificationModal && (
        <div className="modal-backdrop" onClick={() => setShowNotificationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={styles.blockTitle}><FiBell /> Enviar Notificação Push</div>
              <button onClick={() => setShowNotificationModal(false)} className="modal-close">×</button>
            </div>

            {notifMessage.text && (
              <div style={notifMessage.type === 'success' ? styles.alertOk : styles.alertError}>
                {notifMessage.text}
              </div>
            )}

            <div className="modal-body">
              <div className="form-group">
                <label>Título da Notificação</label>
                <input
                  style={styles.input}
                  placeholder="Ex: Nova atualização disponível! 🎉"
                  value={notifTitle}
                  onChange={(e) => setNotifTitle(e.target.value)}
                  maxLength={50}
                />
                <span className="char-count">{notifTitle.length}/50</span>
              </div>

              <div className="form-group">
                <label>Mensagem</label>
                <textarea
                  style={{...styles.input, minHeight: 80, resize: 'vertical'}}
                  placeholder="Digite sua mensagem... Pode usar emojis! 😊"
                  value={notifBody}
                  onChange={(e) => setNotifBody(e.target.value)}
                  maxLength={200}
                  rows={3}
                />
                <span className="char-count">{notifBody.length}/200</span>
              </div>

              <div className="recipient-selector">
                <div className="selector-tabs">
                  <button
                    className={`tab-button ${notifSendToAll ? 'active' : ''}`}
                    onClick={() => setNotifSendToAll(true)}
                  >
                    <FiUsers style={{marginRight: 6}} /> Todos os Usuários
                  </button>
                  <button
                    className={`tab-button ${!notifSendToAll ? 'active' : ''}`}
                    onClick={() => setNotifSendToAll(false)}
                  >
                    <FiSearch style={{marginRight: 6}} /> Usuários Específicos
                  </button>
                </div>

                {!notifSendToAll && (
                  <div className="user-selection-box">
                    <div className="search-box-notif">
                      <FiSearch />
                      <input
                        className="search-input-notif"
                        type="text"
                        placeholder="Buscar por email..."
                        value={notifUserSearch}
                        onChange={(e) => setNotifUserSearch(e.target.value)}
                      />
                    </div>

                    <div className="selected-count">
                      {notifSelectedUsers.size} usuário(s) selecionado(s)
                    </div>

                    <div className="users-list-notif">
                      {notifFilteredUsers.length === 0 ? (
                        <div style={styles.empty}>Nenhum usuário encontrado</div>
                      ) : (
                        notifFilteredUsers.slice(0, 50).map(user => (
                          <div
                            key={user.id}
                            className={`user-item-notif ${notifSelectedUsers.has(user.id) ? 'selected' : ''}`}
                            onClick={() => toggleNotifUser(user.id)}
                          >
                            <input
                              type="checkbox"
                              checked={notifSelectedUsers.has(user.id)}
                              readOnly
                              style={{cursor: 'pointer'}}
                            />
                            <div className="user-info-notif">
                              <span className="user-email">{user.email || 'Sem email'}</span>
                              {user.updatedAt && (
                                <span className="user-date">
                                  {user.updatedAt.toLocaleDateString('pt-BR')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      {notifFilteredUsers.length > 50 && (
                        <div style={styles.blockHint}>
                          Mostrando 50 de {notifFilteredUsers.length} usuários. Refine sua busca.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {notifSendToAll && (
                  <div className="all-users-info">
                    <FiUsers size={48} color="#4a90e2" />
                    <p>A notificação será enviada para <strong>{refDocs.length} usuários</strong></p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowNotificationModal(false)}
                style={{...styles.button, ...styles.ghost}}
                disabled={notifSending}
              >
                Cancelar
              </button>
              <button
                onClick={sendNotification}
                style={{...styles.button, ...styles.primary}}
                disabled={notifSending}
              >
                {notifSending ? 'Enviando...' : (
                  <>
                    <FiBell style={{marginRight: 6}} /> Enviar Notificação
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Card simples */
function Card({ icon, title, value, hint }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardIcon}>{icon}</div>
      <div style={styles.cardMeta}>
        <div style={styles.cardTitle}>{title}</div>
        <div style={styles.cardValue}>{value}</div>
        {hint && <div style={styles.cardHint}>{hint}</div>}
      </div>
    </div>
  );
}

/* ============================
 * ESTILOS BASE
 * ============================ */
const styles = {
  page: {
    padding: 24,
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#f6f7fb',
    minHeight: '100vh',
    color: '#111',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  brand: { display: 'flex', gap: 12, alignItems: 'center' },
  logoCircle: {
    width: 40, height: 40, borderRadius: 12, background: '#111', color: '#fff',
    display: 'grid', placeItems: 'center', fontWeight: 700, letterSpacing: 0.5,
  },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 2 },
  subtitle: { fontSize: 12, color: '#666' },
  headerActions: { display: 'flex', gap: 10 },

  button: {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px',
    borderRadius: 10, border: '1px solid #ddd', background: '#fff',
    cursor: 'pointer', textDecoration: 'none', color: '#111', fontWeight: 600,
  },
  primary: { background: '#1f6feb', color: '#fff', border: '1px solid #1f6feb' },
  secondary: { background: '#fff', color: '#1f6feb', border: '1px solid #1f6feb' },
  ghost: { background: '#fff' },
  icon: { marginTop: 1 },

  error: {
    background: '#ffe8e6', border: '1px solid #ffbdb6', color: '#a0413d',
    padding: '10px 12px', borderRadius: 8, marginBottom: 16,
  },

  cardsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 14, marginBottom: 18,
  },
  card: {
    display: 'flex', gap: 12, background: '#fff', border: '1px solid #eee',
    borderRadius: 14, padding: 14, alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  cardIcon: {
    width: 44, height: 44, borderRadius: 12, background: '#f1f4ff', color: '#1f6feb',
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  cardMeta: { display: 'flex', flexDirection: 'column' },
  cardTitle: { fontSize: 12, color: '#666', marginBottom: 2 },
  cardValue: { fontSize: 18, fontWeight: 800, lineHeight: '22px' },
  cardHint: { fontSize: 12, color: '#888', marginTop: 2 },

  /* ==== Influencer block ==== */
  infBlock: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16, marginBottom: 18,
  },
  alertError: { background: '#ffe8e6', border: '1px solid #ffbdb6', color: '#a0413d', padding: '8px 10px', borderRadius: 8 },
  alertOk: { background: '#eaffea', border: '1px solid #b7efb7', color: '#1a7f1a', padding: '8px 10px', borderRadius: 8 },
  code: { display: 'block', padding: '10px 12px', background: '#f5f6fb', border: '1px solid #e5e7f2', borderRadius: 10, maxWidth: 900, wordBreak: 'break-all' },

  searchBlock: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16, marginBottom: 18,
  },
  searchHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'baseline' },
  blockTitle: { fontWeight: 800 },
  blockHint: { fontSize: 12, color: '#777' },
  searchRow: { display: 'flex', alignItems: 'center', gap: 10 },
  searchInputWrap: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    border: '1px solid #ddd', borderRadius: 10, background: '#fafafa', flex: 1,
  },
  searchInput: { border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: 14 },
  muted: { fontSize: 12, color: '#777' },

  filtersBlock: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16, marginBottom: 16,
  },
  filtersHeader: { marginBottom: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  label: { width: 90, color: '#444', fontWeight: 600 },
  presets: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  presetBtn: {
    padding: '8px 12px', borderRadius: 10, border: '1px solid #ddd', background: '#fafafa',
    cursor: 'pointer', fontWeight: 600,
  },
  presetBtnActive: { background: '#1f6feb', color: '#fff', borderColor: '#1f6feb' },
  periodoLabel: { fontSize: 12, color: '#666', marginLeft: 6 },

  dateInput: { border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', background: '#fafafa' },

  dimensions: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'start' },
  dimCol: { display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'start' },
  dimLabel: { fontSize: 12, color: '#555', fontWeight: 700 },
  dimHelp: { fontSize: 12, color: '#888', marginTop: 2 },

  input: {
    border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', background: '#fff', outline: 'none',
  },

  chipsRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  chip: {
    border: '1px solid #dfe3f0',
    background: '#f5f7ff',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer',
  },

  split: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  splitCol: { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16 },

  analyticCard: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  analyticCardHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 4 },
  analyticCardTitle: { fontSize: 14, fontWeight: 700, color: '#333' },
  analyticCardSub: { fontSize: 12, color: '#888' },

  storeFilterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  storeFilterBtn: {
    padding: '6px 14px', borderRadius: 20, border: '1px solid #ddd', background: '#f5f5f5',
    fontSize: 13, cursor: 'pointer', fontWeight: 500, color: '#555', transition: 'all .15s',
  },
  storeFilterBtnActive: {
    background: '#1f6feb', color: '#fff', border: '1px solid #1f6feb', fontWeight: 700,
  },
  storeStatBox: { background: '#f8f9fa', borderRadius: 10, padding: '10px 12px', textAlign: 'center' },
  storeStatVal: { fontSize: 22, fontWeight: 800, color: '#222', lineHeight: 1.2 },
  storeStatLabel: { fontSize: 11, color: '#888', marginTop: 2 },

  storeMetricCard: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14,
    padding: '18px 16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  storeMetricIcon: { width: 40, height: 40, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 10 },
  storeMetricVal: { fontSize: 24, fontWeight: 800, color: '#111', lineHeight: 1.1, marginBottom: 4 },
  storeMetricLabel: { fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 2 },
  storeMetricSub: { fontSize: 11, color: '#aaa' },
  storeEmptyChart: { height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 13 },

  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  btnRefresh: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
    borderRadius: 10, border: '1px solid #1f6feb', background: '#1f6feb', color: '#fff',
    cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
  storeCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 },
  storeCard: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 18, textAlign: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  storeCardIcon: { fontSize: 28, marginBottom: 8 },
  storeCardLabel: { fontSize: 13, color: '#666', fontWeight: 600, marginBottom: 4 },
  storeCardValue: { fontSize: 28, fontWeight: 800, color: '#111', lineHeight: '34px' },
  storeCardSub: { fontSize: 12, color: '#888', marginBottom: 2 },
  storeCardRevenue: { fontSize: 16, fontWeight: 700, color: '#1f6feb', marginTop: 8 },
  chartCard: { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16 },
  chartTitle: { fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#444' },
  loading: { textAlign: 'center', padding: 40, color: '#888' },
  empty: { textAlign: 'center', padding: 40, color: '#888' },

  topList: { listStyle: 'none', padding: 0, margin: '12px 0 0 0', display: 'grid', gap: 8 },
  topItem: {
    display: 'flex', alignItems: 'center', gap: 10, background: '#fafbff', border: '1px solid #eef1ff',
    padding: '10px 12px', borderRadius: 10, fontWeight: 600,
  },
  badge: { background: '#1f6feb', color: '#fff', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 },
  empty: { color: '#777', padding: '12px 8px', fontSize: 14 },

  /* --- bloco tabela referrer --- */
  refTableBlock: {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 16, marginBottom: 18,
  },
  selBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', marginBottom: 10, borderTop: '1px dashed #eee', borderBottom: '1px dashed #eee',
  },
  selInfo: { fontSize: 13, color: '#444' },
  btnSm: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #1f6feb', background: '#1f6feb',
    color: '#fff', fontWeight: 700, cursor: 'pointer',
  },
  btnSmLight: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d9dbe7', background: '#f5f6fb',
    color: '#111', fontWeight: 600, cursor: 'pointer',
  },
  btnPrimary: {
    padding: '10px 14px', borderRadius: 10, border: '1px solid #1f6feb', background: '#1f6feb',
    color: '#fff', fontWeight: 800, cursor: 'pointer',
  },
  pagination: {
    marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  },

  tableWrap: { overflowX: 'auto', marginTop: 12 },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14 },
  th: { textAlign: 'left', color: '#666', fontWeight: 600, fontSize: 12, borderBottom: '1px solid #eee', padding: '10px 8px' },
  td: { padding: '10px 8px', borderBottom: '1px solid #f2f2f2', whiteSpace: 'nowrap' },
  tdSmall: {
    padding: '10px 8px', borderBottom: '1px solid #f2f2f2', color: '#333',
    maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  tdRight: { padding: '10px 8px', borderBottom: '1px solid #f2f2f2', textAlign: 'right' },

  cta: { display: 'flex', justifyContent: 'center', marginTop: 18, marginBottom: 24 },
  
  // Estilos para grid de imagens
  imagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: 20,
    marginTop: 16,
  },
  imageCard: {
    background: '#fafbff',
    border: '2px solid #e8e9ef',
    borderRadius: 14,
    padding: 16,
    transition: 'all 0.2s',
  },
  alertInfo: {
    background: '#e8f4ff',
    border: '1px solid #b3d9ff',
    color: '#0066cc',
    padding: '8px 12px',
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 13,
    fontWeight: 600,
  },
};

/** estilos auxiliares para cards mobile (tabelas) */
const m = {
  cardRow: {
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 12,
    padding: 12,
    display: 'grid',
    gap: 6,
  },
  rowLine: { fontSize: 14, color: '#222' },
  rowActions: { marginTop: 4 },

  // Modal de Notificações
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eee',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: 32,
    color: '#999',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    padding: 24,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: '1px solid #eee',
  },
  formGroup: {
    marginBottom: 20,
    position: 'relative',
  },
  charCount: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontSize: 11,
    color: '#999',
  },
  recipientSelector: {
    marginTop: 24,
  },
  selectorTabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  tabButton: {
    flex: 1,
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 10,
    background: '#f9f9f9',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  tabActive: {
    background: '#4a90e2',
    color: '#fff',
    borderColor: '#4a90e2',
  },
  userSelectionBox: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: 16,
  },
  searchBoxNotif: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    background: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 12,
  },
  searchInputNotif: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: 14,
  },
  selectedCount: {
    fontSize: 13,
    fontWeight: 600,
    color: '#4a90e2',
    marginBottom: 12,
  },
  usersListNotif: {
    maxHeight: 300,
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  userItemNotif: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  userItemSelected: {
    background: '#e3f2fd',
  },
  userInfoNotif: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
  },
  userEmail: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
  },
  userDate: {
    fontSize: 12,
    color: '#666',
  },
  allUsersInfo: {
    textAlign: 'center',
    padding: 40,
    background: '#f9f9f9',
    borderRadius: 12,
  },
};
